import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import api from "../api/axiosConfig";
import { blogImageSrc, COURSE_PLACEHOLDER } from "../api/blogImage";

const rupee = (value) =>
  `₹${Number(value || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const loadRazorpay = () =>
  new Promise((resolve) => {
    if (window.Razorpay) {
      resolve(true);
      return;
    }
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });

function CourseCheckout() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [checkout, setCheckout] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api
      .get(`/payments/checkout/${id}`)
      .then((res) => {
        if (!cancelled) {
          setCheckout(res.data);
          setError("");
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setCheckout(null);
          setError(err.response?.data?.message || "Unable to load checkout.");
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  const payNow = async () => {
    if (!checkout?.course) return;
    setPaying(true);
    setError("");
    try {
      const res = await api.post("/payments/order", { courseId: checkout.course.id });
      if (res.data.alreadyPaid || res.data.free) {
        navigate("/dashboard", { replace: true });
        return;
      }

      const ready = await loadRazorpay();
      const key = res.data.keyId || process.env.REACT_APP_RAZORPAY_KEY_ID;
      if (!ready || !window.Razorpay || !key) {
        setError("Payment gateway is not available.");
        setPaying(false);
        return;
      }

      const { customer, course } = checkout;
      const razorpay = new window.Razorpay({
        key,
        amount: res.data.order.amount,
        currency: res.data.order.currency || "INR",
        name: "Kademy",
        description: course.title,
        order_id: res.data.order.id,
        prefill: {
          name: customer.name,
          email: customer.email,
        },
        theme: { color: "#3730A3" },
        handler: async (response) => {
          try {
            await api.post("/payments/verify", {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            });
            navigate("/dashboard", { replace: true });
          } catch (err) {
            setError(err.response?.data?.message || "Payment could not be confirmed.");
            setPaying(false);
          }
        },
        modal: {
          ondismiss: () => setPaying(false),
        },
      });
      razorpay.on("payment.failed", () => {
        setError("Payment was not completed.");
        setPaying(false);
      });
      razorpay.open();
    } catch (err) {
      setError(err.response?.data?.message || "Unable to start payment.");
      setPaying(false);
    }
  };

  if (loading) return <p className="text-center py-5">Loading checkout...</p>;
  if (!checkout?.course) {
    return <p className="text-center py-5">{error || "Course not found."}</p>;
  }

  const { course, customer, alreadyPaid } = checkout;

  return (
    <main className="checkout-page">
      <form
        className="checkout-card"
        onSubmit={(event) => {
          event.preventDefault();
          if (!alreadyPaid) payNow();
        }}
      >
        <p className="checkout-back">
          <Link to={`/courses/${course.id}`}>Back to course</Link>
        </p>
        <div className="checkout-course">
          <img
            src={blogImageSrc(course.banner, COURSE_PLACEHOLDER)}
            alt=""
            onError={(event) => {
              event.target.onerror = null;
              event.target.src = blogImageSrc(null, COURSE_PLACEHOLDER);
            }}
          />
          <div>
            <p>Course</p>
            <h1>{course.title}</h1>
          </div>
        </div>

        <label>
          Name
          <input value={customer.name || ""} readOnly />
        </label>
        <label>
          Email
          <input type="email" value={customer.email || ""} readOnly />
        </label>

        <dl className="checkout-bill">
          <div>
            <dt>Price</dt>
            <dd>{rupee(course.price)}</dd>
          </div>
          <div>
            <dt>Tax ({course.tax_percentage}%)</dt>
            <dd>{rupee(course.tax_amount)}</dd>
          </div>
          <div className="checkout-total">
            <dt>Total</dt>
            <dd>{rupee(course.total)}</dd>
          </div>
        </dl>

        {error ? (
          <p className="checkout-error" role="alert">
            {error}
          </p>
        ) : null}

        {alreadyPaid ? (
          <Link to="/dashboard" className="btn-accent checkout-pay">
            Go to dashboard
          </Link>
        ) : (
          <button type="submit" className="btn-accent checkout-pay" disabled={paying}>
            {paying ? "Opening payment..." : "Pay now"}
          </button>
        )}
      </form>
    </main>
  );
}

export default CourseCheckout;
